import React, { useState, useRef } from 'react';
import {
  CropOverlayRoot,
  CropDimRect,
  CropSelection,
  CropHandle,
  CropActionBar,
  CropActionButton,
} from '../imageCanvasStyles';

const MIN_SIZE = 5;
const HANDLES = [ 'tl', 'tc', 'tr', 'ml', 'mr', 'bl', 'bc', 'br' ];

const CropOverlay = ({ imgRef, onApply, onCancel }) => {
  const [ crop, setCrop ] = useState({ x: 10, y: 10, w: 80, h: 80 });
  const overlayRef = useRef(null);
  const dragRef = useRef(null);

  const startDrag = (e, mode) => {
    e.preventDefault();
    e.stopPropagation();
    overlayRef.current?.setPointerCapture(e.pointerId);
    dragRef.current = { mode, startX: e.clientX, startY: e.clientY, initialCrop: { ...crop } };
  };

  const handlePointerMove = e => {
    if (!dragRef.current) return;
    const { mode, startX, startY, initialCrop: ic } = dragRef.current;
    const rect = overlayRef.current?.getBoundingClientRect();
    if (!rect) return;

    const dx = ((e.clientX - startX) / rect.width) * 100;
    const dy = ((e.clientY - startY) / rect.height) * 100;

    let { x, y, w, h } = ic;

    if (mode === 'move') {
      x = Math.max(0, Math.min(100 - w, ic.x + dx));
      y = Math.max(0, Math.min(100 - h, ic.y + dy));
    } else {
      if (mode.includes('l')) {
        const newX = Math.max(0, Math.min(ic.x + ic.w - MIN_SIZE, ic.x + dx));
        w = ic.x + ic.w - newX;
        x = newX;
      }
      if (mode.includes('r')) {
        w = Math.max(MIN_SIZE, Math.min(100 - ic.x, ic.w + dx));
      }
      if (mode.includes('t')) {
        const newY = Math.max(0, Math.min(ic.y + ic.h - MIN_SIZE, ic.y + dy));
        h = ic.y + ic.h - newY;
        y = newY;
      }
      if (mode.includes('b')) {
        h = Math.max(MIN_SIZE, Math.min(100 - ic.y, ic.h + dy));
      }
    }

    setCrop({ x, y, w, h });
  };

  const handlePointerUp = () => {
    dragRef.current = null;
  };

  const handleApply = () => {
    const img = imgRef?.current;
    if (!img) return;

    const rw = img.offsetWidth;
    const rh = img.offsetHeight;
    const scaleX = img.naturalWidth / rw;
    const scaleY = img.naturalHeight / rh;

    const sx = Math.round((crop.x / 100) * rw * scaleX);
    const sy = Math.round((crop.y / 100) * rh * scaleY);
    const sw = Math.round((crop.w / 100) * rw * scaleX);
    const sh = Math.round((crop.h / 100) * rh * scaleY);

    const canvas = document.createElement('canvas');
    canvas.width = sw;
    canvas.height = sh;
    canvas.getContext('2d').drawImage(img, sx, sy, sw, sh, 0, 0, sw, sh);
    onApply(canvas.toDataURL('image/png'));
  };

  const { x, y, w, h } = crop;

  return (
    <CropOverlayRoot ref={overlayRef} onPointerMove={handlePointerMove} onPointerUp={handlePointerUp}>
      <CropDimRect $top='0' $left='0' $right='0' $h={`${y}%`} />
      <CropDimRect $top={`${y + h}%`} $left='0' $right='0' $bottom='0' />
      <CropDimRect $top={`${y}%`} $left='0' $w={`${x}%`} $h={`${h}%`} />
      <CropDimRect $top={`${y}%`} $left={`${x + w}%`} $right='0' $h={`${h}%`} />

      <CropSelection $x={x} $y={y} $w={w} $h={h} onPointerDown={e => startDrag(e, 'move')}>
        {HANDLES.map(pos => (
          <CropHandle key={pos} $pos={pos} onPointerDown={e => startDrag(e, pos)} />
        ))}
      </CropSelection>

      <CropActionBar>
        <CropActionButton onClick={onCancel}>Cancel</CropActionButton>
        <CropActionButton $primary onClick={handleApply}>Apply</CropActionButton>
      </CropActionBar>
    </CropOverlayRoot>
  );
};

export { CropOverlay };
